import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, Search, Filter, ArrowRight } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { adminApi } from '../../lib/api';
import { formatDate, formatPrice, getStatusLabel, getStatusColor } from '../../lib/utils';
import { toast } from 'sonner';

const AdminParcelsPage = () => {
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchParcels();
  }, []);

  const fetchParcels = async () => {
    try {
      const response = await adminApi.getParcels();
      setParcels(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const filteredParcels = parcels.filter(parcel => {
    const matchesFilter = filter === 'all' || parcel.status === filter;
    const matchesSearch = search === '' ||
      parcel.pickup_address.city.toLowerCase().includes(search.toLowerCase()) ||
      parcel.delivery_address.city.toLowerCase().includes(search.toLowerCase()) ||
      parcel.sender_name?.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-parcels-page">
      <div>
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
          Toutes les missions
        </h1>
        <p className="text-slate-600 mt-1">Vue d'ensemble de toutes les livraisons</p>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="created">Créée</SelectItem>
                <SelectItem value="accepted">Acceptée</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="delivered">Livrée</SelectItem>
                <SelectItem value="paid">Payée</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Parcels List */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="spinner mx-auto"></div>
              <p className="text-slate-500 mt-4">Chargement...</p>
            </div>
          ) : filteredParcels.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune mission trouvée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left p-4">Itinéraire</th>
                    <th className="text-left p-4">Expéditeur</th>
                    <th className="text-left p-4">Transporteur</th>
                    <th className="text-left p-4">Statut</th>
                    <th className="text-left p-4">Prix</th>
                    <th className="text-left p-4">Commission</th>
                    <th className="text-left p-4">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParcels.map((parcel) => (
                    <tr key={parcel.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`parcel-${parcel.id}`}>
                      <td className="p-4">
                        <p className="font-medium text-slate-900">
                          {parcel.pickup_address.city} → {parcel.delivery_address.city}
                        </p>
                        <p className="text-sm text-slate-500">{parcel.distance_km} km</p>
                      </td>
                      <td className="p-4 text-slate-600">{parcel.sender_name}</td>
                      <td className="p-4 text-slate-600">{parcel.carrier_name || '-'}</td>
                      <td className="p-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(parcel.status)}`}>
                          {getStatusLabel(parcel.status)}
                        </span>
                      </td>
                      <td className="p-4 font-medium text-slate-900">{formatPrice(parcel.total_price)}</td>
                      <td className="p-4 font-medium text-green-600">{formatPrice(parcel.commission)}</td>
                      <td className="p-4 text-slate-600">{formatDate(parcel.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{parcels.length}</p>
            <p className="text-sm text-slate-500">Total missions</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{parcels.filter(p => p.status === 'created').length}</p>
            <p className="text-sm text-slate-500">En attente</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{parcels.filter(p => ['accepted', 'in_progress'].includes(p.status)).length}</p>
            <p className="text-sm text-slate-500">En cours</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{parcels.filter(p => p.status === 'paid').length}</p>
            <p className="text-sm text-slate-500">Payées</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminParcelsPage;
